import React, { useState, useMemo, useEffect } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import TabsBase from "../../components/reusable/Navigation/TabsBase";
import ContainerBase from "../../components/framepart/SubpageLayout/ContainerBase";
import ToolbarHeadSubpage from "../../components/framepart/SubpageLayout/ToolbarHeadSubpage";

import DropdownMulti from "../../components/reusable/Forms/DropdownMulti";
import DropdownList from "../../components/reusable/Forms/DropdownList";
import TextField from "../../components/reusable/Forms/TextField";
import TextArea from "../../components/reusable/Forms/TextArea";

import Button from "../../components/reusable/Forms/Button";
import Switch from "../../components/reusable/Forms/Switch";
import IconButton from "../../components/reusable/Forms/IconButton";

import RadioGroup from "../../components/reusable/Forms/RadioGroup";
import CheckboxGroup from "../../components/reusable/Forms/CheckboxGroup";
import SliderSelector from "../../components/reusable/Forms/SliderSelector";

import MockEngineViewer from "../../components/advanced/ConfigEngine/mockEngineViewer";

/* ============================================================================
   OPTIONS (référentiels UI / listes)
============================================================================ */

import {
  countryOptions,
  SECTOR_OPTIONS,
  TYPOLOGY_OPTIONS,
  SUB_TYPOLOGY_OPTIONS,
  PROJECT_PHASE_OPTIONS,
  OPERATIONAL_CONTINUITY_OPTIONS,
  DELIVERY_METHOD_OPTIONS,
  STANDARDS_OPTIONS,
  DISCIPLINES_OPTIONS,
  PROJECT_FOOTPRINT_OPTIONS,
  INTERVENTION_NATURE_OPTIONS,
  FUNCTIONAL_ORGANIZATION_OPTIONS,
  DESIGN_MATURITY_OPTIONS,
  BIM_MATURITY_OPTIONS,
  COMPLEXITY_LEVEL_OPTIONS,
  SITE_CONSTRAINTS_OPTIONS,
  INTERFACES_OPTIONS,

  OPERATING_MODE_OPTIONS,
  SERVICE_AVAILABILITY_OPTIONS,
  BUSINESS_CRITICALITY_OPTIONS,
  SERVICE_CONTINUITY_OPTIONS,
  LAYOUT_STRATEGY_OPTIONS,
  MODULARITY_APPROACH_OPTIONS,
  EXPANSION_STRATEGY_OPTIONS,
  INTEGRATION_LEVEL_OPTIONS,
  MAIN_FLOWS_OPTIONS,
  EXTERNAL_CONNECTIONS_OPTIONS,
  AUTOMATION_LEVEL_OPTIONS,
  DATA_MONITORING_IMPORTANCE_OPTIONS,
  SAFETY_CLASSIFICATION_OPTIONS,
  REGULATORY_ENVIRONMENT_OPTIONS,
  AVAILABILITY_TARGET_OPTIONS,
  DESIGN_ROBUSTNESS_OPTIONS,
} from "../../taxonomies/index-scope";


/* ============================================================================
   STYLES (containers + helpers)
============================================================================ */
const baseZoneStyle = {
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  flexShrink: 0,
};

const baseLeftZoneStyle = { ...baseZoneStyle, flex: 3, minWidth: 700 };
const baseRightZoneStyle = { ...baseZoneStyle, flex: 1, minWidth: 350, boxSizing: "border-box" };

const infoTextStyle = { fontFamily: theme.fonts.body, fontSize: theme.fontSizes.sm, color: theme.colors.text.body, lineHeight: 1.4 };
const infoListStyle = { paddingLeft: 18, margin: 0 };
const infoParagraphStyle = { marginTop: 0, marginBottom: 8 };

const grid2ColsStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "min-content", columnGap: 28, rowGap: 18, alignItems: "start" };
const cardStyle = { display: "flex", flexDirection: "column", gap: 10, padding: 14, borderRadius: 8, background: theme.colors.background.surface, boxSizing: "border-box", minHeight: 180 };
const cardTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.semibold, color: theme.colors.text.caneva, paddingBottom: 8, borderBottom: `1px solid ${theme.colors.borders.default}` };

const dropdownBaseProps = { width: "100%", height: 32, gap: 10, padding: "3px 6px" };

/* ============================================================================
   HELPERS (CheckboxGroup)
============================================================================ */
const toggleMultiValue = (prev, value, checked) => {
  if (checked) return Array.from(new Set([...(prev ?? []), value]));
  return (prev ?? []).filter((v) => v !== value);
};

/* ============================================================================
   PAGE
============================================================================ */
const Subpage_101 = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [toolbarCategories, setToolbarCategories] = useState([]);
  const [isCoreLensOpen, setIsCoreLensOpen] = useState(false);
  useEffect(() => setIsCoreLensOpen(false), [activeTab]);

  /* ------------------------------ */
  /* Identity — État local          */
  /* ------------------------------ */
  const [identity, setIdentity] = useState({
    projectName: "",
    projectCode: "",
    description: "",
    contractingAuthority: "",
    projectOwner: "",
    partner: "",
    mainContractor: "",
    ownersEngineer: "",
    leadDesigner: "",
    independentChecker: "",
    bimManager: "",
    pmo: "",
    otherDesigners: "",
    city: "",
    region: "",
    country: "",
  });

  const updateIdentityField = (field, value) => setIdentity((prev) => ({ ...prev, [field]: value }));

  /* ------------------------------ */
  /* Governance — État local        */
  /* ------------------------------ */
  const [governance, setGovernance] = useState({});
  const updateGovernanceField = (field, value) => setGovernance((prev) => ({ ...prev, [field]: value }));

  /* ------------------------------ */
  /* Typology — État local          */
  /* ------------------------------ */
  const [sector, setSector] = useState("");
  const [typology, setTypology] = useState("");
  const [subTypology, setSubTypology] = useState("");

  const [projectPhase, setProjectPhase] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [operationalContinuity, setOperationalContinuity] = useState("");

  const [standards, setStandards] = useState([]);
  const [disciplines, setDisciplines] = useState([]);

  const [designMaturity, setDesignMaturity] = useState("");
  const [bimMaturity, setBimMaturity] = useState("");
  const [complexityLevel, setComplexityLevel] = useState("");

  const [siteConstraints, setSiteConstraints] = useState([]);
  const [interfaces, setInterfaces] = useState([]);

  const [projectFootprint, setProjectFootprint] = useState([]);
  const [interventionNature, setInterventionNature] = useState([]);
  const [functionalOrganization, setFunctionalOrganization] = useState([]);

  const sectorOptions = SECTOR_OPTIONS;
  const typologyOptions = TYPOLOGY_OPTIONS;
  const subTypologyOptionsFiltered = useMemo(() => (!typology ? [] : SUB_TYPOLOGY_OPTIONS.filter((s) => s.parent === typology).map(({ value, label }) => ({ value, label }))), [typology]);

  const operationalContinuityOptions = OPERATIONAL_CONTINUITY_OPTIONS;
  const projectFootprintOptions = PROJECT_FOOTPRINT_OPTIONS;
  const interventionNatureOptions = INTERVENTION_NATURE_OPTIONS;
  const functionalOrganizationOptions = FUNCTIONAL_ORGANIZATION_OPTIONS;

  const designMaturityOptions = DESIGN_MATURITY_OPTIONS;
  const bimMaturityOptions = BIM_MATURITY_OPTIONS;
  const complexityLevelOptions = COMPLEXITY_LEVEL_OPTIONS;

  const siteConstraintsOptions = SITE_CONSTRAINTS_OPTIONS;
  const interfacesOptions = INTERFACES_OPTIONS;

  const projectPhaseOptions = PROJECT_PHASE_OPTIONS;
  const deliveryMethodOptions = DELIVERY_METHOD_OPTIONS;

  const standardsOptions = STANDARDS_OPTIONS;
  const disciplinesOptions = DISCIPLINES_OPTIONS;

  /* ============================================================================
     CONCEPTUAL ARCHITECTURE — STATE
  ============================================================================ */
  const [projectOperatingMode, setProjectOperatingMode] = useState("");
  const [serviceAvailabilityRequirement, setServiceAvailabilityRequirement] = useState("");
  const [businessCriticality, setBusinessCriticality] = useState("");
  const [serviceContinuityStrategy, setServiceContinuityStrategy] = useState("");

  const [overallLayoutStrategy, setOverallLayoutStrategy] = useState("");
  const [modularityApproach, setModularityApproach] = useState("");
  const [futureExpansionStrategy, setFutureExpansionStrategy] = useState("");
  const [systemIntegrationLevel, setSystemIntegrationLevel] = useState("");

  const [mainOperationalFlows, setMainOperationalFlows] = useState([]);
  const [externalSystemConnections, setExternalSystemConnections] = useState([]);
  const [automationLevel, setAutomationLevel] = useState("");
  const [dataMonitoringImportance, setDataMonitoringImportance] = useState("");

  const [safetyClassification, setSafetyClassification] = useState("");
  const [regulatoryEnvironment, setRegulatoryEnvironment] = useState("");
  const [availabilityTarget, setAvailabilityTarget] = useState("");
  const [designRobustnessLevel, setDesignRobustnessLevel] = useState("");

  const mainOperationalFlowsCheckboxOptions = useMemo(
    () =>
      MAIN_FLOWS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        checked: mainOperationalFlows.includes(o.value),
      })),
    [mainOperationalFlows]
  );
  const externalSystemConnectionsCheckboxOptions = useMemo(
    () =>
      EXTERNAL_CONNECTIONS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        checked: externalSystemConnections.includes(o.value),
      })),
    [externalSystemConnections]
  );


  
const [duration, setDuration] = useState(10);
const [typologyConfig, setTypologyConfig] = useState("TYP-BLD-01");
const [start, setStart] = useState("2026-01-01");







  /* ------------------------------ */
  /* Constraints — État local       */
  /* ------------------------------ */
  const [constraints, setConstraints] = useState({});
  const updateConstraintsField = (field, value) => setConstraints((prev) => ({ ...prev, [field]: value }));

  /* ------------------------------ */
  /* Objectives and Risks — État local */
  /* ------------------------------ */
  const [objectives, setObjectives] = useState({});
  const updateObjectivesField = (field, value) => setObjectives((prev) => ({ ...prev, [field]: value }));

  /* ============================================================================
     ZONE A1 & A2 — IDENTITY
  ============================================================================ */
  const zoneA1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 12,
    sections: [
      {
        content: (() => {
          const identityGridStyle = grid2ColsStyle;

          const identityCardStyle = { ...cardStyle, gap: 14, paddingRight: 80 };
          const identityCardTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.normal, color: theme.colors.text.caneva, paddingBottom: 8, borderBottom: `1px solid ${theme.colors.borders.default}` };

          const fieldProps = { width: "100%", height: 32, labelWidth: 150, gap: 12, padding: "3px 6px", marginTop: 0, marginBottom: 0 };
          const compactTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.normal, color: theme.colors.text.caneva, width: 260, marginLeft: 0, borderBottom: `1px solid ${theme.colors.borders.default}`, paddingBottom: 8 };

          return (
            <div style={identityGridStyle}>
              {/* (1) PROJECT IDENTITY */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Project identity</div>

                <TextField label="Project Code" value={identity.projectCode} onChange={(e) => updateIdentityField("projectCode", e.target.value.slice(0, 50))} {...fieldProps} />

                <TextArea label="Project Name" rows={2} resize="none" value={identity.projectName} onChange={(e) => updateIdentityField("projectName", e.target.value)} labelWidth={150} gap={12} />

                <TextArea label="Project Description" rows={2} resize="none" value={identity.description} onChange={(e) => updateIdentityField("description", e.target.value)} labelWidth={150} gap={12} />
              </div>

              {/* (2) STAKEHOLDERS */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Stakeholders</div>

                <TextArea label="Contracting Authority" rows={2} resize="none" value={identity.contractingAuthority} onChange={(e) => updateIdentityField("contractingAuthority", e.target.value)} labelWidth={150} gap={12} />

                <TextArea label="Project Owner" rows={2} resize="none" value={identity.projectOwner} onChange={(e) => updateIdentityField("projectOwner", e.target.value)} labelWidth={150} gap={12} />

                <TextArea label="Partner" rows={2} resize="none" value={identity.partner} onChange={(e) => updateIdentityField("partner", e.target.value)} labelWidth={150} gap={12} />

                <TextArea label="Main Contractor" rows={2} resize="none" value={identity.mainContractor} onChange={(e) => updateIdentityField("mainContractor", e.target.value)} labelWidth={150} gap={12} />
              </div>

              {/* (3) LOCATION */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Location</div>

                <TextField label="City / Location" value={identity.city} onChange={(e) => updateIdentityField("city", e.target.value)} {...fieldProps} />
                <TextField label="Region" value={identity.region} onChange={(e) => updateIdentityField("region", e.target.value)} {...fieldProps} />
                <DropdownList label="Country" options={countryOptions} value={identity.country} onChange={(v) => updateIdentityField("country", v)} {...fieldProps} />
              </div>

              {/* (4) CONTRACT & OVERSIGHT */}
              <div style={identityCardStyle}>
                <div style={compactTitleStyle}>Contract & Oversight</div>

                <TextArea label="Owner’s Engineer" rows={2} resize="none" value={identity.ownersEngineer} onChange={(e) => updateIdentityField("ownersEngineer", e.target.value)} labelWidth={185} gap={12} />
                <TextArea label="Lead Designer" rows={2} resize="none" value={identity.leadDesigner} onChange={(e) => updateIdentityField("leadDesigner", e.target.value)} labelWidth={185} gap={12} />
                <TextArea label="Independent Checker" rows={2} resize="none" value={identity.independentChecker} onChange={(e) => updateIdentityField("independentChecker", e.target.value)} labelWidth={185} gap={12} />
                <TextArea label="BIM Manager" rows={2} resize="none" value={identity.bimManager} onChange={(e) => updateIdentityField("bimManager", e.target.value)} labelWidth={185} gap={12} />
                <TextArea label="Project Management" rows={2} resize="none" value={identity.pmo} onChange={(e) => updateIdentityField("pmo", e.target.value)} labelWidth={185} gap={12} />
                <TextArea label="Other Designers / Specialists" rows={2} resize="none" value={identity.otherDesigners} onChange={(e) => updateIdentityField("otherDesigners", e.target.value)} labelWidth={185} gap={12} />
              </div>
            </div>
          );
        })(),
      },
    ],
  };

  const zoneA2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Cadre général du projet",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet établit le cadre administratif et stratégique du projet. Il constitue la base de référence pour l’ensemble des modules Corelia.</p>
            <ul style={infoListStyle}>
              <li>Définit l’identité officielle et la description du projet.</li>
              <li>Identifie les acteurs clés et les responsabilités principales.</li>
              <li>Précise la localisation et le contexte général d’implantation.</li>
              <li>Fournit un socle stable pour la structuration ultérieure.</li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONES B1 & B2 — GOVERNANCE
  ============================================================================ */
  const zoneB1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 10, sections: [{ title: "Governance", content: <div style={{ display: "flex", flexDirection: "column", gap: 16 }} /> }] };

  const zoneB2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Governance — Rôle dans le cadrage",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>xxxxx</p>
            <ul style={infoListStyle}>
              <li>
                <strong>xx</strong> aaaaaa
              </li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONES C1 & C2 — TYPOLOGY
  ============================================================================ */
  const zoneC1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 12,
    sections: [
      {
        content: (
          <div style={{ ...grid2ColsStyle, paddingBottom: 180 }}>
            {/* (1) PROJECT PROFILE */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Project Profile</div>

              <DropdownList label="Sector" value={sector} onChange={setSector} options={sectorOptions} labelWidth={140} marginTop={2} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList
                label="Typology"
                value={typology}
                onChange={(v) => {
                  setTypology(v);
                  setSubTypology("");
                }}
                options={typologyOptions}
                labelWidth={140}
                marginTop={0}
                marginBottom={6}
                {...dropdownBaseProps}
              />

              <DropdownList label="Sub-typology" value={subTypology} onChange={setSubTypology} options={subTypologyOptionsFiltered} disabled={!typology} labelWidth={140} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownMulti label="Scope of intervention" selectedValues={interventionNature} onChange={setInterventionNature} options={interventionNatureOptions} labelWidth={140} marginTop={0} marginBottom={0} width="100%" height={32} gap={10} />
            </div>

            {/* (2) ASSET FORM */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Asset Form</div>

              <DropdownMulti label="Footprint" selectedValues={projectFootprint} onChange={setProjectFootprint} options={projectFootprintOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={10} marginBottom={6} />

              <DropdownMulti label="Organization" selectedValues={functionalOrganization} onChange={setFunctionalOrganization} options={functionalOrganizationOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={0} marginBottom={6} />

              <DropdownMulti label="Site constraints" selectedValues={siteConstraints} onChange={setSiteConstraints} options={siteConstraintsOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={0} marginBottom={6} />

              <DropdownMulti label="Interfaces" selectedValues={interfaces} onChange={setInterfaces} options={interfacesOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={0} marginBottom={0} />
            </div>

            {/* (3) DELIVERY STRATEGY */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Delivery Strategy</div>

              <DropdownList label="Delivery method" value={deliveryMethod} onChange={setDeliveryMethod} options={deliveryMethodOptions} labelWidth={140} marginTop={2} marginBottom={6} {...dropdownBaseProps} />

              <DropdownMulti label="Project phases" selectedValues={projectPhase} onChange={setProjectPhase} options={projectPhaseOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={0} marginBottom={6} />

              <DropdownList label="Operational context" value={operationalContinuity} onChange={setOperationalContinuity} options={operationalContinuityOptions} labelWidth={140} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>

            {/* (4) ENGINEERING & REQUIREMENTS */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Engineering & Requirements</div>

              <DropdownMulti label="Disciplines" selectedValues={disciplines} onChange={setDisciplines} options={disciplinesOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={10} marginBottom={6} />

              <DropdownMulti label="Standards" selectedValues={standards} onChange={setStandards} options={standardsOptions} width="100%" height={32} labelWidth={140} gap={10} marginTop={0} marginBottom={6} />

              <DropdownList label="BIM maturity" value={bimMaturity} onChange={setBimMaturity} options={bimMaturityOptions} labelWidth={140} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Design maturity" value={designMaturity} onChange={setDesignMaturity} options={designMaturityOptions} labelWidth={140} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Complexity" value={complexityLevel} onChange={setComplexityLevel} options={complexityLevelOptions} labelWidth={140} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>
          </div>
        ),
      },
    ],
  };

  const zoneC2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Guidelines structurants",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet définit les paramètres structurants du projet. Il qualifie le profil, le contexte d’intervention et les contraintes majeures.</p>
            <ul style={infoListStyle}>
              <li>Les choix réalisés ici déterminent les templates activés dans les modules suivants.</li>
              <li>Ils influencent la structuration Breakdown et Systems.</li>
              <li>
                Le <strong>Project Complexity Level</strong> précise le niveau global de complexité.
              </li>
              <li>
                Les <strong>Project Interfaces (Organizational & Operational)</strong> cadrent les interactions clés du projet.
              </li>
              <li>Ces éléments constituent un cadre stable et transversal.</li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONE D1 — CONCEPTUAL ARCHITECTURE
  ============================================================================ */
  const zoneD1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 12,
    sections: [
      {
        content: (
          <div style={{ ...grid2ColsStyle, paddingBottom: 180 }}>
            {/* (1) OPERATING FRAMEWORK */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Operating Framework</div>

              <RadioGroup label="Project Operating Mode" name="projectOperatingMode" value={projectOperatingMode} onChange={setProjectOperatingMode} options={OPERATING_MODE_OPTIONS} orientation="horizontal" labelWidth={180} width="100%" />

              <RadioGroup label="Service Availability Requirement" name="serviceAvailabilityRequirement" value={serviceAvailabilityRequirement} onChange={setServiceAvailabilityRequirement} options={SERVICE_AVAILABILITY_OPTIONS} orientation="horizontal" labelWidth={180} width="100%" />

              <DropdownList label="Business Criticality" value={businessCriticality} onChange={setBusinessCriticality} options={BUSINESS_CRITICALITY_OPTIONS} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Service Continuity Strategy" value={serviceContinuityStrategy} onChange={setServiceContinuityStrategy} options={SERVICE_CONTINUITY_OPTIONS} labelWidth={180} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>

            {/* (2) OVERALL SYSTEM LAYOUT */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Overall System Layout</div>

              <DropdownList label="Overall Layout Strategy" value={overallLayoutStrategy} onChange={setOverallLayoutStrategy} options={LAYOUT_STRATEGY_OPTIONS} labelWidth={180} marginTop={2} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Modularity Approach" value={modularityApproach} onChange={setModularityApproach} options={MODULARITY_APPROACH_OPTIONS} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Future Expansion Strategy" value={futureExpansionStrategy} onChange={setFutureExpansionStrategy} options={EXPANSION_STRATEGY_OPTIONS} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Level of System Integration" value={systemIntegrationLevel} onChange={setSystemIntegrationLevel} options={INTEGRATION_LEVEL_OPTIONS} labelWidth={180} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>

            {/* (3) AUTOMATION & IOT */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Automation & IoT</div>

              <DropdownList label="Automation Level" value={automationLevel} onChange={setAutomationLevel} options={AUTOMATION_LEVEL_OPTIONS} labelWidth={180} marginTop={2} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Data & Monitoring Importance" value={dataMonitoringImportance} onChange={setDataMonitoringImportance} options={DATA_MONITORING_IMPORTANCE_OPTIONS} labelWidth={180} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>

            {/* (4) TECHNICAL & COMPLIANCE */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Technical & Compliance Positioning</div>

              <DropdownList label="Safety Classification" value={safetyClassification} onChange={setSafetyClassification} options={SAFETY_CLASSIFICATION_OPTIONS} labelWidth={180} marginTop={2} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Regulatory Environment" value={regulatoryEnvironment} onChange={setRegulatoryEnvironment} options={REGULATORY_ENVIRONMENT_OPTIONS} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Availability Target" value={availabilityTarget} onChange={setAvailabilityTarget} options={AVAILABILITY_TARGET_OPTIONS} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />

              <DropdownList label="Design Robustness Level" value={designRobustnessLevel} onChange={setDesignRobustnessLevel} options={DESIGN_ROBUSTNESS_OPTIONS} labelWidth={180} marginTop={0} marginBottom={0} {...dropdownBaseProps} />
            </div>

            {/* (5) OPERATIONAL FLOWS */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Operational Flows</div>

              <CheckboxGroup options={mainOperationalFlowsCheckboxOptions} onChange={(val, checked) => setMainOperationalFlows((prev) => toggleMultiValue(prev, val, checked))} orientation="vertical" />
            </div>

            {/* (6) OPERATIONAL CONNECTIONS */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Operational Connections</div>

              <CheckboxGroup options={externalSystemConnectionsCheckboxOptions} onChange={(val, checked) => setExternalSystemConnections((prev) => toggleMultiValue(prev, val, checked))} orientation="vertical" />
            </div>
          </div>
        ),
      },
    ],
  };

  const zoneD2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Positionnement opérationnel",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet formalise la logique globale de fonctionnement du projet avant toute décomposition détaillée.</p>
            <ul style={infoListStyle}>
              <li>
                Le <strong>Project Operating Mode</strong> et la disponibilité attendue définissent le niveau de service.
              </li>
              <li>Le pattern architectural global structure l’organisation des systèmes.</li>
              <li>
                Les <strong>External System Connections</strong> qualifient les interconnexions techniques majeures.
              </li>
              <li>Les niveaux d’automatisation, de criticité et de robustesse orientent la génération des architectures systèmes.</li>
            </ul>
          </div>
        ),
      },
    ],
  };


  

  const zoneE1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 6, sections: [
      { title: "Zone E1 — Vue générale", content: (
      <div>Aucun élément sélectionné.
        
        <MockEngineViewer
          durationMonths={duration}
          onDurationChange={setDuration}
          typologyId={typologyConfig}
          onTypologyChange={setTypologyConfig}
          startDate={start}
          onStartDateChange={setStart}
        />



      </div> 
      ),

      }
    ] 
  };
  const zoneE2 = { style: baseRightZoneStyle, gap: 12, sectionGap: 6, sections: [{ icon: "/assets/icones/container/shield.svg", title: "Zone E2 — Paramètres", content: <div>Configuration non définie…</div> }] };
  const zoneF1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 6, sections: [{ title: "Zone F1 — Vue générale", content: <div>Aucun élément sélectionné.</div> }] };
  const zoneF2 = { style: baseRightZoneStyle, gap: 12, sectionGap: 6, sections: [{ icon: "/assets/icones/container/shield.svg", title: "Zone F2 — Paramètres", content: <div>Configuration non définie…</div> }] };

  /* ============================================================================
     TABS
  ============================================================================ */
  const TabA = (
    <div style={tabStyle}>
      <ContainerBase {...zoneA1} />
      <ContainerBase {...zoneA2} />
    </div>
  );

  const TabB = (
    <div style={tabStyle}>
      <ContainerBase {...zoneB1} />
      <ContainerBase {...zoneB2} />
    </div>
  );

  const TabC = (
    <div style={tabStyle}>
      <ContainerBase {...zoneC1} />
      {/* <ContainerBase {...zoneC2} /> */}
    </div>
  );

  const TabD = (
    <div style={tabStyle}>
      <ContainerBase {...zoneD1} />
      {/* <ContainerBase {...zoneD2} /> */}
    </div>
  );

  const TabE = (
    <div style={tabStyle}>
      <ContainerBase {...zoneE1} />
      <ContainerBase {...zoneE2} />
    </div>
  );

  const TabF = (
    <div style={tabStyle}>
      <ContainerBase {...zoneF1} />
      <ContainerBase {...zoneF2} />
    </div>
  );

  const tabs = [
    { label: "General Information", content: TabA },
    { label: "Typology", content: TabC },
    { label: "Conceptual Architecture", content: TabD },
    { label: "Governance", content: TabB },
    { label: "Phasing", content: TabE },
    { label: "Objectives and Risks", content: TabF },
  ];

  /* ============================================================================
     RENDER
  ============================================================================ */
  return (
    <div style={{ width: "100%", height: "100%", overflowX: "hidden" }}>
      <ToolbarHeadSubpage
        style={{ padding: 24, height: 50, minHeight: 50, overflow: "visible", background: "transparent" }}
        left={[
          <Button key="new" variant="blue" onClick={() => alert("Template — action fictive")}>
            Nouvel élément
          </Button>,
        ]}
        center={[]}
        right={[
          <IconButton key="corelens" icon="/assets/icones/info.svg" variant="white" size="32" rounded="true" onClick={() => setIsCoreLensOpen((v) => !v)} />,
          <Switch key="switch" label="Actifs" enabled={switchOn} onChange={setSwitchOn} showText={false} size="small" />,
        ]}
      />

      <TabsBase tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

/* ============================================================================
   STYLE COMMUN TABS
============================================================================ */
const tabStyle = { display: "flex", flexDirection: "row", gap: 10, padding: 10, overflowY: "clip", overflowX: "hidden", minWidth: "fit-content", boxSizing: "border-box" };

export default Subpage_101;
